import type { ReactNode } from 'react';
import { useState } from 'react';
import {
  CalendarIcon,
  Card,
  CardContent,
  CardHeader,
  ImportIcon,
  SuccessIcon,
} from '@forge/common';
import type { TimelineEvent, TimelineEventType } from '@/api/dashboard';
import ShowMoreToggle from './ShowMoreToggle';

interface ActivityFeedProps {
  events: TimelineEvent[];
  limit?: number;
}

const eventMeta: Record<TimelineEventType, { icon: ReactNode; ring: string }> = {
  milestone_completed: {
    icon: (
      <SuccessIcon
        className="h-4 w-4 text-green-600 dark:text-green-400"
        aria-hidden
      />
    ),
    ring: 'bg-green-100 dark:bg-green-900',
  },
  release: {
    icon: (
      <ImportIcon
        className="h-4 w-4 text-blue-600 dark:text-blue-400"
        aria-hidden
      />
    ),
    ring: 'bg-blue-100 dark:bg-blue-900',
  },
  roadmap_change: {
    icon: (
      <CalendarIcon
        className="h-4 w-4 text-orange-600 dark:text-orange-400"
        aria-hidden
      />
    ),
    ring: 'bg-orange-100 dark:bg-orange-900',
  },
};

const dateFormatter = new Intl.DateTimeFormat('en', {
  month: 'short',
  day: 'numeric',
});

const ActivityFeed = ({ events, limit = 6 }: ActivityFeedProps) => {
  const [expanded, setExpanded] = useState(false);

  const sortedEvents = [...events].sort((left, right) => {
    return new Date(right.date).getTime() - new Date(left.date).getTime();
  });

  const visibleEvents = expanded ? sortedEvents : sortedEvents.slice(0, limit);
  const hiddenCount = sortedEvents.length - sortedEvents.slice(0, limit).length;

  return (
    <Card className="h-full border border-gray-200/80 shadow-sm dark:border-gray-800">
      <CardHeader
        title="Recent activity"
        subtitle="Milestones, releases, and roadmap updates"
      />
      <CardContent top="none">
        {visibleEvents.length === 0 ? (
          <p className="text-sm text-color-tertiary">No recent activity.</p>
        ) : (
          <ol className="space-y-5">
            {visibleEvents.map((event, index) => {
              const meta = eventMeta[event.type];
              const isLast = index === visibleEvents.length - 1;

              return (
                <li
                  key={`${event.date}-${event.title}-${index}`}
                  className="relative flex gap-3"
                >
                  <div className="flex flex-col items-center">
                    <span
                      className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${meta.ring}`}
                    >
                      {meta.icon}
                    </span>
                    {isLast ? undefined : (
                      <span className="mt-1 w-px flex-1 bg-gray-200 dark:bg-gray-800" />
                    )}
                  </div>
                  <div className="min-w-0 flex-1 pb-1">
                    <div className="flex items-center justify-between gap-2">
                      <p className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">
                        {event.title}
                      </p>
                      <span className="shrink-0 text-xs text-color-tertiary">
                        {dateFormatter.format(new Date(event.date))}
                      </span>
                    </div>
                    <p className="mt-0.5 line-clamp-2 text-sm leading-6 text-color-tertiary">
                      {event.description}
                    </p>
                  </div>
                </li>
              );
            })}
          </ol>
        )}
        {hiddenCount > 0 ? (
          <ShowMoreToggle
            expanded={expanded}
            onToggle={() => setExpanded((current) => !current)}
            moreLabel={`Show all ${sortedEvents.length} updates`}
          />
        ) : undefined}
      </CardContent>
    </Card>
  );
};

export type { ActivityFeedProps };
export default ActivityFeed;
